import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, usersAPI, departmentsAPI, commercialConfigAPI, settingsAPI, rolesAPI, notificationsAPI, integrationsAPI, trainingAPI, approvalFlowAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FaDownload, FaEdit, FaPlus, FaSearch, FaUsers, FaUpload, FaUserCheck, FaUserSlash, FaSyncAlt, FaGraduationCap } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import ManageDepartmentsModal from '../components/ManageDepartmentsModal';
import PasswordInput from '../components/PasswordInput';
import { ActionsMenu } from '../components/ActionsMenu';
import EmployeeMatchModal from '../components/EmployeeMatchModal';
import { StatCard } from '../components/UnifiedUIComponents';
import { Modal, ModalFooter } from '../components/Modals';
import TablePagination from '../components/TablePagination';

const EMPTY_FORM = Object.freeze({
    username: '',
    password: '',
    confirm_password: '',
    email: '',
    employee_id: '',
    employee_name: '',
    designation: '',
    department_id: '',
    phone_number: '',
    default_plant_id: '',
    default_line_id: '',
    manager_id: '',
    hire_date: '',
    role: 'User',
    is_active: true,
    new_password: '',
    confirm_new_password: ''
});

// Fallback only — used until the live Role Master list loads from the API
// (see rolesAPI / backend/services/roles.js). Roles are now admin-managed
// rows, not a fixed set; this list exists purely to avoid an empty dropdown
// flash on first paint.
const FALLBACK_ROLE_OPTIONS = ['Super Admin', 'Admin', 'QA Head', 'QA', 'HOD', 'Manager', 'User'];
const PASSWORD_POLICY_MESSAGE = 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character.';
const isPasswordCompliant = (value) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])\S{8,}$/.test(String(value || ''));

// Initials avatar (no photo storage yet) — deterministic color from the name so
// the same person looks the same across reloads without persisting anything.
const AVATAR_COLORS = ['#1890ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#f5222d', '#2f54eb'];
const initialsOf = (name) => String(name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';
const avatarColorFor = (name) => {
    const str = String(name || '');
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};
const Avatar = ({ name, size = 28 }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: avatarColorFor(name), color: '#fff', fontWeight: 700,
        fontSize: size * 0.4, marginRight: '8px', verticalAlign: 'middle',
    }}>
        {initialsOf(name)}
    </span>
);

const promptForActionESign = (actionLabel) => {
    const password = window.prompt(`Enter your login password to ${actionLabel}:`);
    if (!password) return null;
    const comment = window.prompt(`Enter reason/comment to ${actionLabel}:`);
    if (!comment || !comment.trim()) {
      toast.error('Electronic signature reason/comment is required');
      return null;
    }
    return { password, comment: comment.trim() };
};

const Users = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [plants, setPlants] = useState([]);
    const [lines, setLines] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [roleOptions, setRoleOptions] = useState(FALLBACK_ROLE_OPTIONS);
    const [complianceByUser, setComplianceByUser] = useState({});
    const [trainingDetailUser, setTrainingDetailUser] = useState(null);
    const [trainingDetailRows, setTrainingDetailRows] = useState(null);
    const [eligibilityByUser, setEligibilityByUser] = useState({});
    const [approvalDetailUser, setApprovalDetailUser] = useState(null);
    const [approvalDetailRows, setApprovalDetailRows] = useState(null);
    const [addingDesignation, setAddingDesignation] = useState(false);
    const [newDesignation, setNewDesignation] = useState('');
    const [loading, setLoading] = useState(true);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
    const [showESignModal, setShowESignModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [syncingDepartments, setSyncingDepartments] = useState(false);
    // Employee / department sources switched on in Integration Hub.
    const [integrationRuntime, setIntegrationRuntime] = useState({});
    const [syncingEmployees, setSyncingEmployees] = useState('');
    const [matchSource, setMatchSource] = useState(null);
    const [editingUserId, setEditingUserId] = useState(null);
    const [alertPrefs, setAlertPrefs] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [pendingUserPayload, setPendingUserPayload] = useState(null);
    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [eSignData, setESignData] = useState({ password: '', comment: '' });
    const initialUserRef = useRef(null);
    // Snapshot the form the moment the modal opens so unchanged edits can skip the e-signature.
    useEffect(() => {
        initialUserRef.current = showUserModal ? JSON.stringify(formData) : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUserModal]);

    // New state for modern table
    const [selectedUsers, setSelectedUsers] = useState([]);
    const mobileImportInputRef = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState('username');
    const [sortOrder, setSortOrder] = useState('asc');
    const [roleFilter, setRoleFilter] = useState('All');
    const [activeFilter, setActiveFilter] = useState('All'); // All | Active | Inactive
    const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);

    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch (error) {
            return {};
        }
    }, []);

    const currentUserRole = useMemo(() => (currentUser?.role || '').toLowerCase(), [currentUser]);
    const isSuperAdmin = currentUserRole === 'super admin';
    const isAdmin = currentUserRole === 'admin';

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!formData.department_id) return;
        const selectedDepartment = departments.find((dep) => String(dep.department_id) === String(formData.department_id));
        if (selectedDepartment?.department_name) {
            setDepartmentSearchTerm(selectedDepartment.department_name);
        }
    }, [formData.department_id, departments]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [usersResponse, departmentsResponse, runtimeResponse, settingsResponse, rolesResponse, complianceResponse, eligibilityResponse] = await Promise.all([
                usersAPI.getAll(),
                departmentsAPI.getAll(),
                commercialConfigAPI.getRuntime().catch(() => null),
                settingsAPI.getAll().catch(() => null),
                rolesAPI.getAll().catch(() => null),
                trainingAPI.getComplianceMatrix().catch(() => null),
                approvalFlowAPI.getEligibilityMatrix().catch(() => null)
            ]);
            setUsers(Array.isArray(usersResponse.data?.data) ? usersResponse.data.data : []);
            const complianceRows = Array.isArray(complianceResponse?.data?.data) ? complianceResponse.data.data : [];
            setComplianceByUser(Object.fromEntries(complianceRows.map((c) => [String(c.user_id), c])));
            const eligibilityRows = Array.isArray(eligibilityResponse?.data?.data) ? eligibilityResponse.data.data : [];
            setEligibilityByUser(Object.fromEntries(eligibilityRows.map((e) => [String(e.user_id), e.count])));
            setDepartments(Array.isArray(departmentsResponse.data?.data) ? departmentsResponse.data.data : []);
            setPlants(Array.isArray(runtimeResponse?.data?.data?.plants) ? runtimeResponse.data.data.plants : []);
            setLines(Array.isArray(runtimeResponse?.data?.data?.lines) ? runtimeResponse.data.data.lines : []);
            setIntegrationRuntime(runtimeResponse?.data?.data?.integrations || {});
            const activeRoles = Array.isArray(rolesResponse?.data?.data)
                ? rolesResponse.data.data.filter((r) => r.is_active).map((r) => r.role_name)
                : null;
            if (activeRoles && activeRoles.length) setRoleOptions(activeRoles);
            try {
                const parsed = JSON.parse(settingsResponse?.data?.data?.designation_options || '[]');
                setDesignationOptions(Array.isArray(parsed) ? parsed : []);
            } catch { setDesignationOptions([]); }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEditingUserId(null);
        setFormData({ ...EMPTY_FORM });
        setDepartmentSearchTerm('');
        setShowDepartmentDropdown(false);
    };

    const closeUserModal = () => {
        setShowUserModal(false);
        resetForm();
    };

    const openAddModal = () => {
        if (!isSuperAdmin && !isAdmin) {
            toast.error('You do not have permission to add users.');
            return;
        }
        resetForm();
        setShowUserModal(true);
    };

    const openTrainingDetail = async (user) => {
        setTrainingDetailUser(user);
        setTrainingDetailRows(null);
        try {
            const res = await trainingAPI.getUserCompliance(user.user_id);
            setTrainingDetailRows(res?.data?.data || []);
        } catch (error) {
            toast.error('Failed to load training compliance for this user');
            setTrainingDetailRows([]);
        }
    };

    const openApprovalDetail = async (user) => {
        setApprovalDetailUser(user);
        setApprovalDetailRows(null);
        try {
            const res = await approvalFlowAPI.getUserEligibility(user.user_id);
            setApprovalDetailRows(res?.data?.data || []);
        } catch (error) {
            toast.error('Failed to load approval eligibility for this user');
            setApprovalDetailRows([]);
        }
    };

    const openEditModal = (user) => {
        // A Super Admin account (status, password, role) is changed only by a
        // Super Admin — the API refuses it for everyone else too.
        if (String(user.role || '').toLowerCase() === 'super admin' && !isSuperAdmin) {
            toast.info('Only a Super Admin can change a Super Admin account.');
            return;
        }
        const matchedDepartment = departments.find((dep) => String(dep.department_id) === String(user.department_id));
        setEditingUserId(user.user_id);
        setFormData({
            username: user.username || '',
            password: '',
            confirm_password: '',
            email: user.email || '',
            employee_id: user.employee_id || '',
            employee_name: user.employee_name || '',
            designation: user.designation || '',
            department_id: user.department_id || '',
            phone_number: user.phone_number || '',
            default_plant_id: user.default_plant_id || '',
            default_line_id: user.default_line_id || '',
            manager_id: user.manager_id || '',
            hire_date: user.hire_date ? String(user.hire_date).slice(0, 10) : '',
            role: user.role || 'User',
            is_active: Boolean(user.is_active),
            new_password: '',
            confirm_new_password: ''
        });
        setDepartmentSearchTerm(matchedDepartment?.department_name || user.department_name || '');
        setAlertPrefs(null);
        notificationsAPI.getUserPrefs(user.user_id).then((res) => setAlertPrefs(res.data.data)).catch(() => setAlertPrefs(null));
        setShowUserModal(true);
    };

    const saveAlertPrefs = async () => {
        try {
            await notificationsAPI.saveUserPrefs(editingUserId, alertPrefs);
            toast.success('Alert settings saved');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to save alert settings');
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const employeeSources = Object.entries(integrationRuntime)
        .filter(([, item]) => item?.features?.employee_sync)
        .map(([key, item]) => ({ key, label: item.label }));
    const departmentSources = Object.entries(integrationRuntime)
        .filter(([key, item]) => (key === 'aiventra_lms' ? item?.features?.department_sync : item?.features?.employee_sync))
        .map(([key, item]) => ({ key, label: item.label }));

    const syncDepartmentsFrom = async (source) => {
        try {
            setSyncingDepartments(true);
            const response = await integrationsAPI.syncDepartments(source.key);
            const refreshed = await departmentsAPI.getAll();
            setDepartments(Array.isArray(refreshed.data?.data) ? refreshed.data.data : []);
            toast.success(response.data?.message || `Departments synchronized from ${source.label}`);
        } catch (error) {
            toast.error(error?.response?.data?.message || `Could not fetch departments from ${source.label}`);
        } finally {
            setSyncingDepartments(false);
        }
    };

    // Links employee IDs / designations from the external system onto existing users.
    const syncEmployeesFrom = async (source) => {
        try {
            setSyncingEmployees(source.key);
            const response = await integrationsAPI.syncEmployees(source.key);
            toast.success(response.data?.message || `Employees synchronized from ${source.label}`, { autoClose: 8000 });
            await loadData();
        } catch (error) {
            toast.error(error?.response?.data?.message || `Could not sync employees from ${source.label}`);
        } finally {
            setSyncingEmployees('');
        }
    };

    const validateUserForm = () => {
        // Validations from original component
        if (!editingUserId && !formData.username.trim()) {
            toast.error('Username is required');
            return false;
        }
        if (!editingUserId && !formData.password) {
            toast.error('Password is required');
            return false;
        }
        if (!editingUserId && !isPasswordCompliant(formData.password)) {
            toast.error(PASSWORD_POLICY_MESSAGE);
            return false;
        }
        if (!editingUserId && formData.password !== formData.confirm_password) {
            toast.error('Password and confirm password must match');
            return false;
        }
        if (!formData.email.trim()) {
            toast.error('Email is required');
            return false;
        }
        if (!formData.employee_id.trim()) {
            toast.error('Employee ID is required');
            return false;
        }
        if (!formData.employee_name.trim()) {
            toast.error('Employee Name is required');
            return false;
        }
        if (!formData.department_id) {
            toast.error('Department is required');
            return false;
        }
        if (!formData.role) {
            toast.error('Role is required');
            return false;
        }
        const hasAnyResetPasswordInput = formData.new_password || formData.confirm_new_password;
        if (editingUserId && hasAnyResetPasswordInput) {
            if (!formData.new_password || !formData.confirm_new_password) {
                toast.error('Both new password and confirm new password are required for reset');
                return false;
            }
            if (!isPasswordCompliant(formData.new_password)) {
                toast.error(PASSWORD_POLICY_MESSAGE);
                return false;
            }
            if (formData.new_password !== formData.confirm_new_password) {
                toast.error('New password and confirm new password must match');
                return false;
            }
        }
        return true;
    };

    const prepareSubmit = () => {
        if (!validateUserForm()) return;

        const payload = {
            email: formData.email.trim(),
            employee_id: formData.employee_id.trim(),
            employee_name: formData.employee_name.trim(),
            designation: formData.designation.trim(),
            department_id: formData.department_id,
            phone_number: formData.phone_number.trim(),
            default_plant_id: formData.default_plant_id || null,
            default_line_id: formData.default_line_id || null,
            manager_id: formData.manager_id || null,
            hire_date: formData.hire_date || null,
            role: formData.role,
            is_active: Boolean(formData.is_active)
        };

        if (!editingUserId) {
            payload.username = formData.username.trim();
            payload.password = formData.password;
        }
        if (editingUserId && formData.new_password) {
            payload.new_password = formData.new_password;
        }

        if (editingUserId && initialUserRef.current === JSON.stringify(formData)) {
            toast.info('No changes were made — nothing to save, so no signature is required.');
            return;
        }

        setSearchTerm('');
        setPendingUserPayload(payload);
        setESignData({ password: '', comment: '' });
        setShowESignModal(true);
    };

    const handleConfirmESignAndSave = async () => {
        // Logic from original component
        if (!pendingUserPayload) return;
        if (!eSignData.password) {
            toast.error('Electronic signature password is required');
            return;
        }
        if (!eSignData.comment.trim()) {
            toast.error('Electronic signature reason/comment is required');
            return;
        }

        const payload = {
            ...pendingUserPayload,
            signature_password: eSignData.password,
            signature_comment: eSignData.comment.trim()
        };

        try {
            setSaving(true);
            const reauthResponse = await authAPI.reauth({
                password: eSignData.password,
                action: editingUserId ? 'Update user record' : 'Create user record',
                comment: eSignData.comment.trim()
            });
            const reauthToken = reauthResponse?.data?.data?.reauth_token;
            if (!reauthToken) {
                toast.error('Re-authentication failed');
                setSaving(false);
                return;
            }
            const requestConfig = {
                headers: { 'x-reauth-token': reauthToken }
            };

            if (editingUserId) {
                await usersAPI.update(editingUserId, payload, requestConfig);
                toast.success('User updated successfully');
            } else {
                await usersAPI.create(payload, requestConfig);
                toast.success('User created successfully');
            }

            setShowESignModal(false);
            setPendingUserPayload(null);
            setESignData({ password: '', comment: '' });
            closeUserModal();
            await loadData();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to save user');
        } finally {
            setSaving(false);
        }
    };
    
    // Filtering, Sorting, Pagination
    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    const sortedAndFilteredUsers = useMemo(() => {
        let result = [...users];

        // Filtering
        const needle = searchTerm.trim().toLowerCase();
        if (needle) {
            result = result.filter(user =>
                [user.username, user.email, user.employee_id, user.employee_name, user.designation, user.department_name, user.role]
                    .filter(Boolean)
                    .some(value => String(value).toLowerCase().includes(needle))
            );
        }
        if (roleFilter !== 'All') {
            result = result.filter(user => user.role === roleFilter);
        }
        if (activeFilter !== 'All') {
            result = result.filter(user => Boolean(user.is_active) === (activeFilter === 'Active'));
        }
        
        // Sorting
        result.sort((a, b) => {
            const aVal = a[sortBy] || '';
            const bVal = b[sortBy] || '';
            if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [users, searchTerm, roleFilter, activeFilter, sortBy, sortOrder]);

    const userCounts = useMemo(() => {
        const active = users.filter(u => u.is_active).length;
        return { total: users.length, active, inactive: users.length - active };
    }, [users]);
    const toggleActiveCard = (value) => setActiveFilter(cur => (cur === value ? 'All' : value));

    useEffect(() => { setCurrentPage(1); }, [searchTerm, roleFilter, activeFilter]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedAndFilteredUsers.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedAndFilteredUsers, currentPage, itemsPerPage]);


    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUsers(paginatedUsers.map(u => u.user_id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const exportUsersToExcel = () => {
        // Logic from original component
        if (!sortedAndFilteredUsers.length) {
            toast.error('No users available to export');
            return;
        }
        const rows = sortedAndFilteredUsers.map((user, index) => ({
            'Sr. No': index + 1,
            Username: user.username || '',
            Email: user.email || '',
            'Employee ID': user.employee_id || '',
            'Employee Name': user.employee_name || '',
            Designation: user.designation || '',
            Department: user.department_name || '',
            Role: user.role || '',
            Status: user.is_active ? 'Active' : 'Inactive',
            'Created Date': user.created_at ? new Date(user.created_at).toLocaleDateString() : ''
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
        XLSX.writeFile(workbook, `Users_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Users exported to Excel');
    };

    const downloadImportTemplate = () => {
        const templateRows = [
            {
                username: 'john.doe',
                password: 'Password123!',
                email: 'john.doe@example.com',
                employee_id: 'EMP001',
                employee_name: 'John Doe',
                designation: 'QA Officer',
                department_name: 'Quality Assurance',
                phone_number: '123-456-7890',
                role: 'User',
                is_active: 'Yes'
            },
            {
                username: 'jane.smith',
                password: 'Password123!',
                email: 'jane.smith@example.com',
                employee_id: 'EMP002',
                employee_name: 'Jane Smith',
                designation: 'Production Manager',
                department_name: 'Production',
                phone_number: '098-765-4321',
                role: 'Manager',
                is_active: 'Yes'
            }
        ];

        const instructions = [
            ['User Import Template'],
            [],
            ['Fill one user per row.'],
            ['Password must be at least 8 characters and include uppercase, lowercase, number, and special character.'],
            ['Department Name must match an existing department.'],
            ['Role must be one of: Super Admin, Admin, Manager, User.'],
            ['is_active should be "Yes" or "No".'],
            ['Bulk import requires electronic signature.'],
            ['Do not change header names in the User Details sheet.']
        ];

        const wsTemplate = XLSX.utils.json_to_sheet(templateRows);
        const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);

        wsTemplate['!autofilter'] = {
            ref: XLSX.utils.encode_range({
                s: { r: 0, c: 0 },
                e: { r: templateRows.length, c: Object.keys(templateRows[0]).length - 1 }
            })
        };
        wsTemplate['!cols'] = Object.keys(templateRows[0]).map((key) => ({ wch: Math.min(Math.max(key.length + 4, 16), 32) }));
        wsInstructions['!cols'] = [{ wch: 90 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsTemplate, 'User Details');
        XLSX.utils.book_append_sheet(wb, wsInstructions, 'Read Me');
        XLSX.writeFile(wb, 'user_import_template.xlsx');
        toast.success('User import template downloaded');
    };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!window.confirm('Bulk import will create new user records. Continue?')) {
            e.target.value = '';
            return;
        }

        const signature = promptForActionESign('bulk import users');
        if (!signature) {
            e.target.value = '';
            return;
        }

        setLoading(true);
        try {
            let rawRows = [];
            if (file.name.endsWith('.csv')) {
                rawRows = await new Promise((resolve, reject) => {
                    Papa.parse(file, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => resolve(results.data || []),
                        error: reject
                    });
                });
            } else {
                const buffer = await file.arrayBuffer();
                const wb = XLSX.read(buffer, { type: 'array' });
                const sheetName = wb.SheetNames.find(name => String(name).trim().toLowerCase() === 'user details') || wb.SheetNames[0];
                rawRows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { defval: '' });
            }

            if (!Array.isArray(rawRows) || rawRows.length === 0) {
                toast.error('No import rows found in file');
                return;
            }

            const departmentMap = departments.reduce((acc, dep) => {
                acc[String(dep.department_name).toLowerCase()] = dep.department_id;
                return acc;
            }, {});

            let successCount = 0;
            const failures = [];

            for (const row of rawRows) {
                const normalizedRow = Object.fromEntries(Object.entries(row).map(([k, v]) => [String(k).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''), v]));
                
                const departmentId = departmentMap[String(normalizedRow.department_name || '').toLowerCase()];
                if (!departmentId) {
                    failures.push(`User ${normalizedRow.username || normalizedRow.employee_id}: Invalid department name "${normalizedRow.department_name}"`);
                    continue;
                }

                const payload = {
                    username: normalizedRow.username,
                    password: normalizedRow.password,
                    email: normalizedRow.email,
                    employee_id: normalizedRow.employee_id,
                    employee_name: normalizedRow.employee_name,
                    designation: normalizedRow.designation,
                    department_id: departmentId,
                    phone_number: normalizedRow.phone_number,
                    role: normalizedRow.role,
                    is_active: String(normalizedRow.is_active).toLowerCase() === 'yes',
                    signature_password: signature.password,
                    signature_comment: signature.comment
                };

                try {
                    await usersAPI.create(payload);
                    successCount++;
                } catch (error) {
                    failures.push(`User ${normalizedRow.username || normalizedRow.employee_id}: ${error.response?.data?.message || 'Failed to create'}`);
                }
            }

            if (successCount > 0) {
                toast.success(`Successfully imported ${successCount} user(s).`);
                loadData();
            }
            if (failures.length > 0) {
                toast.warn(`Failed to import ${failures.length} user(s). First few errors: ${failures.slice(0, 3).join(' | ')}`);
            }
        } catch (error) {
            toast.error(error.message || 'Import failed');
        } finally {
            setLoading(false);
            e.target.value = ''; // Clear file input
        }
    };
    
    if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

    const renderSortIndicator = (column) => {
        const isActive = sortBy === column;
        return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '6px', lineHeight: 0.75, verticalAlign: 'middle' }}>
                <span style={{ color: isActive && sortOrder === 'asc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)' }}>{'\u25B2'}</span>
                <span style={{ color: isActive && sortOrder === 'desc' ? 'var(--primary-color, #2563eb)' : 'var(--text-light)', fontSize: 'var(--fs-2xs)', marginTop: '-2px' }}>{'\u25BC'}</span>
            </span>
        );
    };

    const SortableHeader = ({ children, column }) => (
        <th onClick={() => handleSort(column)} style={{ cursor: 'pointer' }}>
            {children}{renderSortIndicator(column)}
        </th>
    );

    return (
        <div>
            <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                        <h2 className="page-title" style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FaUsers style={{ color: 'var(--primary-color, #2563eb)' }} />
                            User Management
                        </h2>
                        <p className="page-subtitle">Manage user accounts, roles, and access permissions.</p>
                    </div>
                    <div className="toolbar-full" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button className="btn btn-outline" onClick={exportUsersToExcel}>
                            <FaDownload /> Export
                        </button>
                        <button className="btn btn-outline" onClick={downloadImportTemplate}>
                            <FaDownload /> Import Template
                        </button>
                        <label className="btn btn-info" style={{ margin: 0, cursor: 'pointer' }}>
                            <FaUpload /> Import
                            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} style={{ display: 'none' }} />
                        </label>
                        {(isSuperAdmin || isAdmin) && employeeSources.map((source) => (
                            <button key={source.key} className="btn btn-outline" onClick={() => syncEmployeesFrom(source)} disabled={!!syncingEmployees} title={`Link employee IDs and designations from ${source.label} to matching users (by employee ID, then e-mail)`}>
                                <FaSyncAlt className={syncingEmployees === source.key ? 'fa-spin' : ''} /> {syncingEmployees === source.key ? 'Syncing…' : `Sync Employees (${source.label})`}
                            </button>
                        ))}
                        {(isSuperAdmin || isAdmin) && employeeSources.map((source) => (
                            <button key={`match-${source.key}`} className="btn btn-outline" onClick={() => setMatchSource(source)} title={`Review and link each user to a ${source.label} employee ID`}>
                                <FaUserCheck /> Match Employees ({source.label})
                            </button>
                        ))}
                        {(isSuperAdmin || isAdmin) && (
                            <button className="btn btn-primary" onClick={openAddModal}>
                                <FaPlus /> Add User
                            </button>
                        )}
                    </div>
                    <div className="toolbar-compact" style={{ gap: '8px' }}>
                        {(isSuperAdmin || isAdmin) && (
                            <button className="btn btn-primary" onClick={openAddModal}>
                                <FaPlus /> Add User
                            </button>
                        )}
                        <ActionsMenu
                            title="More actions"
                            items={[
                                { label: 'Export', icon: <FaDownload />, onClick: exportUsersToExcel },
                                { label: 'Import Template', icon: <FaDownload />, onClick: downloadImportTemplate },
                                { label: 'Import', icon: <FaUpload />, onClick: () => mobileImportInputRef.current?.click() },
                                ...employeeSources.map((source) => ({ label: `Sync Employees (${source.label})`, icon: <FaSyncAlt />, onClick: () => syncEmployeesFrom(source), show: isSuperAdmin || isAdmin })),
                                ...employeeSources.map((source) => ({ label: `Match Employees (${source.label})`, icon: <FaUserCheck />, onClick: () => setMatchSource(source), show: isSuperAdmin || isAdmin })),
                            ]}
                        />
                        <input ref={mobileImportInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} style={{ display: 'none' }} />
                    </div>
                </div>

                <div className="stat-card-grid">
                    <StatCard compact filterHint title="Total Users" value={userCounts.total} icon={FaUsers} color="primary" active={activeFilter === 'All' && roleFilter === 'All'} onClick={() => { setActiveFilter('All'); setRoleFilter('All'); }} hint="Show all users" />
                    <StatCard compact filterHint title="Active" value={userCounts.active} icon={FaUserCheck} color="success" active={activeFilter === 'Active'} onClick={() => toggleActiveCard('Active')} hint="Filter: active users" />
                    <StatCard compact filterHint title="Inactive" value={userCounts.inactive} icon={FaUserSlash} color="danger" active={activeFilter === 'Inactive'} onClick={() => toggleActiveCard('Inactive')} hint="Filter: inactive users" />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ flex: '1 1 200px', minWidth: '160px', position: 'relative' }}>
                        <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-light)' }} />
                        <input
                            className="form-control"
                            style={{ paddingLeft: '35px' }}
                            placeholder="Search by name, email, role..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
                        <label style={{ margin: 0, whiteSpace: 'nowrap', fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>Role:</label>
                        <select className="form-control" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ minWidth: '140px', width: 'auto' }}>
                            <option value="All">All Roles</option>
                            {roleOptions.map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                    </div>
                </div>

                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th><input type="checkbox" onChange={handleSelectAll} checked={selectedUsers.length === paginatedUsers.length && paginatedUsers.length > 0} /></th>
                                <SortableHeader column="username">Username</SortableHeader>
                                <SortableHeader column="email">Email</SortableHeader>
                                <SortableHeader column="employee_id">Employee ID</SortableHeader>
                                <SortableHeader column="employee__name">Employee Name</SortableHeader>
                                <SortableHeader column="designation">Designation</SortableHeader>
                                <SortableHeader column="department_name">Department</SortableHeader>
                                <SortableHeader column="role">Role</SortableHeader>
                                <th>Training</th>
                                <th>Can Approve</th>
                                <SortableHeader column="last_login_at">Last Login</SortableHeader>
                                <SortableHeader column="is_active">Status</SortableHeader>
                                <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedUsers.length === 0 && (
                                <tr>
                                    <td colSpan={13} className="text-center">No users found</td>
                                </tr>
                            )}
                            {paginatedUsers.map((user) => (
                                <tr key={user.user_id} onClick={() => openEditModal(user)} style={{ cursor: 'pointer' }}>
                                    <td><input type="checkbox" checked={selectedUsers.includes(user.user_id)} onChange={(e) => {e.stopPropagation(); handleSelectUser(user.user_id)}} /></td>
                                    <td><Avatar name={user.employee_name || user.username} /><strong>{user.username}</strong></td>
                                    <td>{user.email || '-'}</td>
                                    <td>{user.employee_id || '-'}</td>
                                    <td>{user.employee_name || '-'}</td>
                                    <td>{user.designation || '-'}</td>
                                    <td>{user.department_name || '-'}</td>
                                    <td><span className={`badge badge-${(user.role || '').toLowerCase().replace(' ', '-')}`}>{user.role || '-'}</span></td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        {(() => {
                                            const c = complianceByUser[String(user.user_id)];
                                            if (!c || c.total === 0) return <span className="text-muted">-</span>;
                                            const issues = c.missing + c.expired;
                                            const badgeClass = issues > 0 ? 'badge-danger' : c.pending > 0 ? 'badge-warning' : 'badge-success';
                                            return (
                                                <button type="button" className={`badge ${badgeClass}`} style={{ border: 'none', cursor: 'pointer' }}
                                                    title="View training compliance detail" onClick={() => openTrainingDetail(user)}>
                                                    {c.compliant}/{c.total}{issues > 0 ? ` (${issues} gap${issues === 1 ? '' : 's'})` : ''}
                                                </button>
                                            );
                                        })()}
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        {(() => {
                                            const count = eligibilityByUser[String(user.user_id)];
                                            if (!count) return <span className="text-muted">-</span>;
                                            return (
                                                <button type="button" className="badge badge-info" style={{ border: 'none', cursor: 'pointer' }}
                                                    title="View what this user can approve" onClick={() => openApprovalDetail(user)}>
                                                    {count} activit{count === 1 ? 'y' : 'ies'}
                                                </button>
                                            );
                                        })()}
                                    </td>
                                    <td style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                        {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}
                                    </td>
                                    <td>
                                        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                                            {user.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="action-cell" style={{ width: '120px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                            <button className="btn btn-sm btn-outline" onClick={(e) => {e.stopPropagation(); openEditModal(user)}} title="Edit user">
                                                <FaEdit />
                                            </button>
                                            <button className="btn btn-sm btn-outline" onClick={(e) => {e.stopPropagation(); navigate('/training', { state: { filterUsername: user.username } });}} title="View training records">
                                                <FaGraduationCap />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <TablePagination total={sortedAndFilteredUsers.length} page={currentPage} pageSize={itemsPerPage} onPageChange={setCurrentPage} onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }} itemLabel="users" />
            </div>

            {trainingDetailUser && (
                <Modal isOpen size="md" title={`Training Compliance — ${trainingDetailUser.employee_name || trainingDetailUser.username}`} onClose={() => { setTrainingDetailUser(null); setTrainingDetailRows(null); }}>
                    {trainingDetailRows === null ? (
                        <div className="text-center" style={{ padding: '20px' }}>Loading...</div>
                    ) : trainingDetailRows.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)' }}>No mandatory training applies to this user's role.</p>
                    ) : (
                        <table style={{ width: '100%', fontSize: 'var(--fs-sm)', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Training</th>
                                    <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Last Completed</th>
                                    <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Expires</th>
                                    <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid var(--border-color)' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trainingDetailRows.map((r) => (
                                    <tr key={r.training_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ padding: '6px 10px', fontWeight: 600 }}>{r.training_name}</td>
                                        <td style={{ padding: '6px 10px' }}>{r.training_date ? new Date(r.training_date).toLocaleDateString() : '-'}</td>
                                        <td style={{ padding: '6px 10px' }}>{r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : '-'}</td>
                                        <td style={{ padding: '6px 10px' }}>
                                            <span className={`badge ${r.status === 'Compliant' ? 'badge-success' : r.status === 'Pending Approval' ? 'badge-warning' : 'badge-danger'}`}>{r.status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <ModalFooter>
                        <button className="btn btn-outline" type="button" onClick={() => navigate('/training', { state: { filterUsername: trainingDetailUser.username } })}>Open in Training</button>
                    </ModalFooter>
                </Modal>
            )}

            {approvalDetailUser && (
                <Modal isOpen size="md" title={`Approval Eligibility — ${approvalDetailUser.employee_name || approvalDetailUser.username}`} onClose={() => { setApprovalDetailUser(null); setApprovalDetailRows(null); }}>
                    {approvalDetailRows === null ? (
                        <div className="text-center" style={{ padding: '20px' }}>Loading...</div>
                    ) : approvalDetailRows.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)' }}>This user is not named or role-matched on any Approval Flow level.</p>
                    ) : (
                        <div>
                            {approvalDetailRows.map((r) => (
                                <div key={r.workflowType} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderBottom: '1px solid var(--border-color)' }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{r.label}</div>
                                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{r.group}</div>
                                    </div>
                                    <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', textAlign: 'right' }}>{r.levels.join(', ')}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <ModalFooter>
                        <button className="btn btn-outline" type="button" onClick={() => navigate('/settings?tab=approval')}>Open Approval Flow Settings</button>
                    </ModalFooter>
                </Modal>
            )}

            {showUserModal && (
                <Modal isOpen={showUserModal} size="lg" title={editingUserId ? 'Edit User' : 'Add User'} onClose={closeUserModal}>

                            <div className="modal-section">
                              <h3 className="modal-section-title">Employee Details</h3>
                              <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Employee ID *</label>
                                    <input className="form-control" value={formData.employee_id} onChange={(e) => handleFieldChange('employee_id', e.target.value)} placeholder="Enter employee id" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Employee Name *</label>
                                    <input className="form-control" value={formData.employee_name} onChange={(e) => handleFieldChange('employee_name', e.target.value)} placeholder="Enter employee name" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Designation</label>
                                    {addingDesignation ? (
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <input
                                                className="form-control"
                                                value={newDesignation}
                                                onChange={(e) => setNewDesignation(e.target.value)}
                                                placeholder="New designation name"
                                                autoFocus
                                            />
                                            <button type="button" className="btn btn-outline" onClick={async () => {
                                                const name = newDesignation.trim();
                                                if (!name) return;
                                                const next = [...new Set([...designationOptions, name])].sort();
                                                try {
                                                    await settingsAPI.updateSetting('designation_options', JSON.stringify(next));
                                                    setDesignationOptions(next);
                                                    handleFieldChange('designation', name);
                                                    setAddingDesignation(false);
                                                    setNewDesignation('');
                                                } catch (error) {
                                                    toast.error('Failed to save new designation');
                                                }
                                            }}>Add</button>
                                            <button type="button" className="btn btn-outline" onClick={() => { setAddingDesignation(false); setNewDesignation(''); }}>Cancel</button>
                                        </div>
                                    ) : (
                                        <select className="form-control" value={formData.designation} onChange={(e) => {
                                            if (e.target.value === '__add_new__') { setAddingDesignation(true); return; }
                                            handleFieldChange('designation', e.target.value);
                                        }}>
                                            <option value="">Select designation</option>
                                            {formData.designation && !designationOptions.includes(formData.designation) && (
                                                <option value={formData.designation}>{formData.designation}</option>
                                            )}
                                            {designationOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                                            <option value="__add_new__">+ Add new designation…</option>
                                        </select>
                                    )}
                                    <small className="text-muted">Job title (distinct from system Role below). Can be auto-filled from AiventraLMS sync.</small>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Email *</label>
                                    <input type="email" className="form-control" value={formData.email} onChange={(e) => handleFieldChange('email', e.target.value)} placeholder="Enter email address" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Phone Number</label>
                                    <input className="form-control" value={formData.phone_number} onChange={(e) => handleFieldChange('phone_number', e.target.value)} placeholder="Enter phone number" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Reports To (Manager)</label>
                                    <select className="form-control" value={formData.manager_id} onChange={(e) => handleFieldChange('manager_id', e.target.value)}>
                                        <option value="">— None —</option>
                                        {users.filter((u) => u.user_id !== editingUserId).map((u) => (
                                            <option key={u.user_id} value={u.user_id}>{u.employee_name || u.username}</option>
                                        ))}
                                    </select>
                                    <small className="text-muted">Org chart / reporting line — who this user's manager is.</small>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Hire Date</label>
                                    <input type="date" className="form-control" value={formData.hire_date} onChange={(e) => handleFieldChange('hire_date', e.target.value)} />
                                </div>
                              </div>
                            </div>
                            <div className="modal-section">
                              <h3 className="modal-section-title">Account &amp; Access</h3>
                              <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Username *</label>
                                    <input className="form-control" value={formData.username} onChange={(e) => handleFieldChange('username', e.target.value)} placeholder="Enter username" disabled={Boolean(editingUserId)} />
                                </div>
                                {!editingUserId && (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label">Password *</label>
                                            <PasswordInput value={formData.password} onChange={(e) => handleFieldChange('password', e.target.value)} placeholder="Enter temporary password" autoComplete="new-password" />
                                            <small className="text-muted">{PASSWORD_POLICY_MESSAGE}</small>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Confirm Password *</label>
                                            <PasswordInput value={formData.confirm_password} onChange={(e) => handleFieldChange('confirm_password', e.target.value)} placeholder="Re-enter password" autoComplete="new-password" />
                                        </div>
                                    </>
                                )}
                                <div className="form-group">
                                    <label className="form-label">Department *</label>
                                    <div>
                                        <div style={{ position: 'relative', minWidth: 0 }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search and select department..."
                                                value={departmentSearchTerm}
                                                onChange={(e) => {
                                                    const nextValue = e.target.value;
                                                    setDepartmentSearchTerm(nextValue);
                                                    if (!nextValue) handleFieldChange('department_id', '');
                                                }}
                                                onFocus={() => setShowDepartmentDropdown(true)}
                                                onBlur={() => setTimeout(() => setShowDepartmentDropdown(false), 200)}
                                            />
                                            {showDepartmentDropdown && (
                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: 'var(--card-bg, white)', border: '1px solid var(--border-color)', borderRadius: '6px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                                    {departments
                                                        .filter((dep) => String(dep.department_name || '').toLowerCase().includes(departmentSearchTerm.toLowerCase()))
                                                        .map((dep) => (
                                                            <div
                                                                key={dep.department_id}
                                                                onMouseDown={(e) => {
                                                                    e.preventDefault();
                                                                    handleFieldChange('department_id', dep.department_id);
                                                                    setDepartmentSearchTerm(dep.department_name || '');
                                                                    setShowDepartmentDropdown(false);
                                                                }}
                                                                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--card-hover-bg, #f9fafb)'}
                                                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                            >
                                                                {dep.department_name}
                                                            </div>
                                                        ))}
                                                    {departments.filter((dep) => String(dep.department_name || '').toLowerCase().includes(departmentSearchTerm.toLowerCase())).length === 0 && (
                                                        <div style={{ padding: '8px 12px', color: 'var(--text-light)' }}>No departments found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                                            <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowDepartmentsModal(true)} title="Add / Manage Department">
                                                <FaEdit /> Add Dept
                                            </button>
                                            {departmentSources.map((source) => (
                                                <button key={source.key} type="button" className="btn btn-sm btn-outline" onClick={() => syncDepartmentsFrom(source)} disabled={syncingDepartments} title={`Import department names from ${source.label}`}>
                                                    <FaSyncAlt className={syncingDepartments ? 'fa-spin' : ''} /> {syncingDepartments ? 'Syncing…' : `Sync ${source.label}`}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Default Unit</label>
                                    <select
                                        className="form-control"
                                        value={formData.default_plant_id}
                                        onChange={(e) => { handleFieldChange('default_plant_id', e.target.value); handleFieldChange('default_line_id', ''); }}
                                    >
                                        <option value="">Not set — unrestricted (sees the unit picker like an Admin)</option>
                                        {plants.map((plant) => <option key={plant.plant_id} value={plant.plant_id}>{plant.plant_name}</option>)}
                                    </select>
                                    <small className="text-muted">Admin, Super Admin and Corporate roles can still view all units regardless of this setting. For every other role, setting this locks the user to this unit server-side — they can no longer switch to or view other units' data.</small>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Default Section / Line</label>
                                    <select
                                        className="form-control"
                                        value={formData.default_line_id}
                                        onChange={(e) => handleFieldChange('default_line_id', e.target.value)}
                                        disabled={!formData.default_plant_id}
                                    >
                                        <option value="">All sections</option>
                                        {lines
                                            .filter((line) => {
                                                const plant = plants.find((p) => String(p.plant_id) === String(formData.default_plant_id));
                                                return plant && String(line.plant_code).toLowerCase() === String(plant.plant_code).toLowerCase();
                                            })
                                            .map((line) => <option key={line.line_id} value={line.line_id}>{line.line_name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Role</label>
                                    <select className="form-control" value={formData.role} onChange={(e) => handleFieldChange('role', e.target.value)}>
                                        {roleOptions.filter((role) => isSuperAdmin || role.toLowerCase() !== 'super admin').map((role) => <option key={role} value={role}>{role}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Status</label>
                                    <select className="form-control" value={formData.is_active ? 'active' : 'inactive'} onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                                {editingUserId && (
                                    <>
                                        <div className="form-group">
                                            <label className="form-label">Reset Password (Optional)</label>
                                            <PasswordInput value={formData.new_password} onChange={(e) => handleFieldChange('new_password', e.target.value)} placeholder="Enter new password" autoComplete="new-password" />
                                            <small className="text-muted">{PASSWORD_POLICY_MESSAGE}</small>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Confirm New Password</label>
                                            <PasswordInput value={formData.confirm_new_password} onChange={(e) => handleFieldChange('confirm_new_password', e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" />
                                        </div>
                                    </>
                                )}
                              </div>
                            </div>

                            {editingUserId && alertPrefs && (
                                <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <strong>Alerts</strong>
                                        <button type="button" className="btn btn-sm btn-outline" onClick={saveAlertPrefs}>Save alert settings</button>
                                    </div>
                                    <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                        {[['email', 'Email'], ['sms', 'SMS'], ['whatsapp', 'WhatsApp'], ['telegram', 'Telegram']].map(([k, label]) => (
                                            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <input type="checkbox" checked={alertPrefs[k] !== false} onChange={(e) => setAlertPrefs((p) => ({ ...p, [k]: e.target.checked }))} /> {label}
                                            </label>
                                        ))}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label">WhatsApp number</label>
                                            <input className="form-control" value={alertPrefs.whatsapp_number || ''} onChange={(e) => setAlertPrefs((p) => ({ ...p, whatsapp_number: e.target.value }))} placeholder="Blank = use phone number" />
                                        </div>
                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label className="form-label">Telegram chat ID</label>
                                            <input className="form-control" value={alertPrefs.telegram_chat_id || ''} onChange={(e) => setAlertPrefs((p) => ({ ...p, telegram_chat_id: e.target.value }))} placeholder="e.g. 123456789" />
                                        </div>
                                    </div>
                                    <small className="text-muted">SMS uses the phone number above, with country code (e.g. +91…). Channels are configured in Settings → Alerts &amp; Notifications.</small>
                                </div>
                            )}

                        <ModalFooter>
                            <button className="btn btn-outline" onClick={closeUserModal}>Cancel</button>
                            <button className="btn btn-primary" onClick={prepareSubmit} disabled={saving}>
                                {saving ? 'Saving...' : (editingUserId ? 'Update User' : 'Create User')}
                            </button>
                        </ModalFooter>
                </Modal>
            )}

            {showDepartmentsModal && (
                <ManageDepartmentsModal
                    departments={departments}
                    setDepartments={setDepartments}
                    closeModal={() => setShowDepartmentsModal(false)}
                />
            )}

            {showESignModal && (
                <div className="modal-overlay" onClick={() => setShowESignModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2 className="modal-title">Electronic Signature Required</h2>
                            <button className="modal-close" onClick={() => setShowESignModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p>
                                {editingUserId
                                    ? 'Updating user details requires electronic signature and reason.'
                                    : 'Creating a new user requires electronic signature and reason.'}
                            </p>
                            <div className="form-group">
                                <label className="form-label">Password</label>
                                <PasswordInput value={eSignData.password} onChange={(e) => setESignData((prev) => ({ ...prev, password: e.target.value }))} placeholder="Enter your current password" autoComplete="current-password" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Reason / Comment</label>
                                <textarea className="form-control" rows={3} value={eSignData.comment} onChange={(e) => setESignData((prev) => ({ ...prev, comment: e.target.value }))} placeholder={editingUserId ? 'Reason for user update' : 'Reason for user creation'} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-outline" onClick={() => setShowESignModal(false)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleConfirmESignAndSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {matchSource && <EmployeeMatchModal source={matchSource} onClose={() => setMatchSource(null)} onSaved={loadData} />}
        </div>
    );
};

export default Users;
